import { Theme } from '@mui/material/styles';
import { Trans } from 'react-i18next';

import { Icon } from '@/components/Icon/Icon';

import { useTranslate } from '@/hooks/useTranslate';

import { Language } from '@/types/i18n';

import { IconName } from '../Icon';
import { SecurityInfo } from './components/SecurityInfo';

import {
  GuideContainer,
  GuideContent,
  GuideStep,
  GuideStepsContainer,
  GuideTitle,
  IconWrapper,
  TextHighlight,
  TextNormal,
} from './OnboardingGuide.styled';

export type OnboardingGuideConfig = {
  title: string;
  steps: {
    icon: {
      name: IconName;
    };
    renderText: string | ((language: Language) => React.ReactNode);
  }[];
};
const DEFAULT_CONFIG: OnboardingGuideConfig = {
  title: 'onboardingGuide.title',
  steps: [
    {
      icon: {
        name: 'assign',
      },
      renderText: (language: Language) => (
        <Trans
          i18nKey="onboardingGuide.uploadPersonalDoc"
          lang={language}
          components={{
            a1: <TextHighlight />,
            a2: <TextHighlight />,
          }}
        />
      ),
    },
    {
      icon: {
        name: 'camera',
      },
      renderText: (language: Language) => (
        <Trans
          i18nKey="onboardingGuide.uploadSelfie"
          lang={language}
          components={{
            a1: <TextHighlight />,
            a2: <TextHighlight />,
          }}
        />
      ),
    },
    {
      icon: {
        name: 'question-circle',
      },
      renderText: (language: Language) => (
        <Trans
          i18nKey="onboardingGuide.selectAccount"
          lang={language}
          components={{
            a1: <TextHighlight />,
            a2: <TextHighlight />,
            a3: <TextHighlight />,
            a4: <TextHighlight />,
          }}
        />
      ),
    },
    {
      icon: {
        name: 'signature',
      },
      renderText: (language: Language) => (
        <Trans
          i18nKey="onboardingGuide.reviewConfirm"
          lang={language}
          components={{
            a1: <TextHighlight />,
          }}
        />
      ),
    },
  ],
};

type OnboardingGuideProps = {
  config?: OnboardingGuideConfig;
};

export function OnboardingGuide({
  config = DEFAULT_CONFIG,
}: Readonly<OnboardingGuideProps>) {
  const { t, language } = useTranslate();
  return (
    <GuideContainer>
      <GuideContent>
        <GuideTitle>{t(config.title)}</GuideTitle>

        <GuideStepsContainer>
          {config.steps.map(step => (
            <GuideStep key={step.icon.name}>
              <IconWrapper>
                <Icon
                  size={24}
                  color={(theme: Theme) => theme.palette.violet[300]}
                  {...step.icon}
                  name={step.icon.name as IconName}
                />
              </IconWrapper>
              <TextNormal>
                {typeof step.renderText === 'function'
                  ? step.renderText(language)
                  : step.renderText}
              </TextNormal>
            </GuideStep>
          ))}
        </GuideStepsContainer>
      </GuideContent>

      <SecurityInfo />
    </GuideContainer>
  );
}
